<template>
  <div class="kt-portlet kt-portlet--mobile">
    <div class="kt-portlet__head">
      <div class="kt-portlet__head-label">
        <h3 class="kt-portlet__head-title">SendEmailReportAgent — Manage</h3>
      </div>
      <div class="kt-portlet__head-toolbar">
        <router-link to="/Monitoring/ScheduledJobs" class="btn btn-secondary btn-sm">
          <i class="fas fa-arrow-left"></i> กลับ
        </router-link>
      </div>
    </div>
    <div class="kt-portlet__body">
      <!-- ส่งใหม่ -->
      <div class="card mb-3">
        <div class="card-header"><strong>ส่งใหม่ (Resend)</strong></div>
        <div class="card-body">
          <div class="row">
            <div class="col-xl-3 col-md-4 col-sm-6">
              <div class="form-group">
                <label class="label-required">วันที่ของข้อมูล (Period):</label>
                <datepicker
                  :format="datePickerFormat"
                  name="dataPeriodPicker"
                  v-model="periodDate"
                  input-class="form-control"
                  :disabled-dates="disabledDates"
                ></datepicker>
                <small class="form-text text-muted">
                  จะแปลงเป็น <code>{{ periodYmd || '-' }}</code> (yyyyMMdd)
                </small>
              </div>
            </div>
            <div class="col-xl-3 col-md-4 col-sm-6">
              <div class="form-group">
                <label>โหมด:</label>
                <div>
                  <label class="radio-inline mr-3">
                    <input type="radio" v-model="sendMode" value="all" /> ส่งทุก Agent
                  </label>
                  <label class="radio-inline">
                    <input type="radio" v-model="sendMode" value="single" /> เลือก 1 Agent
                  </label>
                </div>
              </div>
            </div>
            <div v-if="sendMode === 'single'" class="col-xl-4 col-md-6 col-sm-12">
              <div class="form-group">
                <label class="label-required">Agent:</label>
                <select class="form-control" v-model="selectedAgent" :disabled="isLoadingAgents">
                  <option value="">-- เลือก agent --</option>
                  <option v-for="a in agents" :key="a.code" :value="a.code">
                    [{{ a.code }}] {{ a.name }}
                  </option>
                </select>
                <small class="form-text text-muted" v-if="isLoadingAgents">โหลด agent...</small>
                <small class="form-text text-muted" v-else>{{ agents.length }} agents ที่ active + IsSendReport</small>
              </div>
            </div>
            <div class="col-12">
              <button class="btn btn-primary" @click="onSend" :disabled="!canSend || isSending">
                <i class="fas fa-paper-plane"></i>
                {{ isSending ? 'กำลังส่ง...' : 'ส่งเมล์' }}
              </button>
              <small class="text-muted ml-2" v-if="!periodYmd">เลือกวันที่ก่อน</small>
              <small class="text-muted ml-2" v-else-if="sendMode === 'single' && !selectedAgent">เลือก agent ก่อน</small>
            </div>
          </div>

          <!-- result -->
          <div v-if="sendResult" class="alert mt-3" :class="sendResultClass">
            <strong>{{ sendResultLabel }}</strong>
            <div class="mt-1"><small>{{ sendResultMessage }}</small></div>
          </div>
        </div>
      </div>

      <!-- History -->
      <div class="card mb-3">
        <div class="card-header d-flex justify-content-between align-items-center">
          <strong>History — รายการรอบส่งล่าสุด (Batch level)</strong>
          <button class="btn btn-sm btn-outline-secondary" @click="loadBatchHistory" :disabled="isLoadingBatch">
            <i class="fas fa-sync-alt"></i> Refresh
          </button>
        </div>
        <div class="card-body p-0">
          <table class="table table-sm table-striped mb-0">
            <thead>
              <tr>
                <th class="text-center">#</th>
                <th class="text-center">StartTime</th>
                <th class="text-center">EndTime</th>
                <th class="text-center">Duration(s)</th>
                <th class="text-center">Status</th>
                <th class="text-center">Items (S/F/Skip/Total)</th>
                <th>Trigger</th>
                <th>Metadata</th>
                <th>Error</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!batchHistory.length">
                <td colspan="9" class="text-center text-muted py-3">ไม่มีประวัติ</td>
              </tr>
              <tr v-for="(h, i) in batchHistory" :key="h.logId">
                <td class="text-center">{{ i + 1 }}</td>
                <td class="text-center">{{ formatDateTime(h.startTime) }}</td>
                <td class="text-center">{{ formatDateTime(h.endTime) }}</td>
                <td class="text-center">{{ h.durationMs != null ? (h.durationMs / 1000).toFixed(1) : '-' }}</td>
                <td class="text-center">
                  <span class="badge" :class="statusBadgeClass(h.status)">{{ h.status }}</span>
                </td>
                <td class="text-center">
                  <span v-if="h.totalItems != null">
                    {{ h.successItems || 0 }} / {{ h.failedItems || 0 }} / {{ h.skippedItems || 0 }} / {{ h.totalItems }}
                  </span>
                  <span v-else>-</span>
                </td>
                <td><small>{{ h.triggerSource }}</small></td>
                <td><small class="text-muted">{{ h.metadata || '-' }}</small></td>
                <td><small class="text-danger">{{ h.errorMessage }}</small></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Per-agent log -->
      <div class="card">
        <div class="card-header d-flex justify-content-between align-items-center">
          <strong>Per-agent log (EmailReportLog)</strong>
          <div>
            <input type="text" class="form-control form-control-sm d-inline-block" style="width:180px"
              placeholder="filter yyyyMMdd" v-model="perAgentFilter" />
            <button class="btn btn-sm btn-outline-secondary ml-2" @click="loadPerAgent" :disabled="isLoadingPerAgent">
              <i class="fas fa-search"></i> Search
            </button>
          </div>
        </div>
        <div class="card-body p-0">
          <table class="table table-sm table-striped mb-0">
            <thead>
              <tr>
                <th class="text-center">#</th>
                <th class="text-center">StartTime</th>
                <th class="text-center">EndTime</th>
                <th>Agent</th>
                <th>Email</th>
                <th class="text-center">IsSuccess</th>
                <th>Error</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!perAgentRows.length">
                <td colspan="7" class="text-center text-muted py-3">ไม่มีข้อมูล</td>
              </tr>
              <tr v-for="(r, i) in perAgentRows" :key="r.id">
                <td class="text-center">{{ i + 1 }}</td>
                <td class="text-center">{{ formatDateTime(r.startTime) }}</td>
                <td class="text-center">{{ formatDateTime(r.endTime) }}</td>
                <td>[{{ r.agentCodeSAP }}] {{ r.agentName }}</td>
                <td><small>{{ r.email }}</small></td>
                <td class="text-center">
                  <span class="badge" :class="r.isSuccess ? 'badge-success' : 'badge-danger'">
                    {{ r.isSuccess ? 'YES' : 'NO' }}
                  </span>
                </td>
                <td><small class="text-danger">{{ r.errorMessage }}</small></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Datepicker from "vuejs-datepicker";

export default {
  components: { Datepicker },
  data() {
    return {
      datePickerFormat: "dd/MM/yyyy",
      periodDate: null,
      sendMode: "all",
      selectedAgent: "",
      agents: [],
      isLoadingAgents: false,
      isSending: false,
      sendResult: null,
      disabledDates: { from: new Date() }, // ห้ามเลือกอนาคต
      batchHistory: [],
      isLoadingBatch: false,
      perAgentRows: [],
      perAgentFilter: "",
      isLoadingPerAgent: false,
    };
  },
  computed: {
    periodYmd() {
      if (!this.periodDate) return "";
      var d = new Date(this.periodDate);
      var y = d.getFullYear();
      var m = String(d.getMonth() + 1).padStart(2, "0");
      var day = String(d.getDate()).padStart(2, "0");
      return y + m + day;
    },
    canSend() {
      if (!this.periodYmd) return false;
      if (this.sendMode === "single" && !this.selectedAgent) return false;
      return true;
    },
    sendResultClass() {
      if (!this.sendResult) return "";
      return this.sendResult.ok ? "alert-success" : "alert-danger";
    },
    sendResultLabel() {
      if (!this.sendResult) return "";
      return this.sendResult.ok ? "✓ ส่งเสร็จสิ้น" : "✗ ส่งไม่สำเร็จ";
    },
    sendResultMessage() {
      return this.sendResult ? this.sendResult.message : "";
    },
  },
  mounted() {
    this.loadAgents();
    this.loadBatchHistory();
    this.loadPerAgent();
  },
  methods: {
    loadAgents() {
      var self = this;
      self.isLoadingAgents = true;
      axios
        .get("/Api/Alert/agents")
        .then(function (r) { self.agents = r.data.items || []; })
        .catch(function (e) { console.error("agents load fail", e); })
        .finally(function () { self.isLoadingAgents = false; });
    },
    loadBatchHistory() {
      var self = this;
      self.isLoadingBatch = true;
      axios
        .get("/Api/Monitor/scheduled_jobs_history", { params: { jobName: "Alert/SendEmailReportAgent", top: 50 } })
        .then(function (r) { self.batchHistory = r.data.rows || []; })
        .catch(function (e) { console.error("batch history fail", e); })
        .finally(function () { self.isLoadingBatch = false; });
    },
    loadPerAgent() {
      var self = this;
      var params = {};
      if (self.perAgentFilter) params.dataperiod = self.perAgentFilter;
      self.isLoadingPerAgent = true;
      axios
        .get("/Api/Alert/emailreport_per_agent_history", { params: params })
        .then(function (r) { self.perAgentRows = r.data.items || []; })
        .catch(function (e) { console.error("per-agent fail", e); })
        .finally(function () { self.isLoadingPerAgent = false; });
    },
    onSend() {
      var self = this;
      if (!self.canSend) return;
      self.isSending = true;
      self.sendResult = null;

      var params = { dataperiod: self.periodYmd };
      if (self.sendMode === "single") params.agent = self.selectedAgent;

      axios
        .get("/Api/Alert/SendEmailReportAgent", { params: params })
        .then(function (r) {
          self.sendResult = { ok: true, message: r.data };
          // reload history
          self.loadBatchHistory();
          self.loadPerAgent();
        })
        .catch(function (e) {
          var data = (e.response && e.response.data) || {};
          var msg = data.error || data.message || e.message;
          self.sendResult = { ok: false, message: msg };
        })
        .finally(function () { self.isSending = false; });
    },
    statusBadgeClass(s) {
      if (s === "SUCCESS") return "badge-success";
      if (s === "FAILED") return "badge-danger";
      if (s === "PARTIAL") return "badge-warning";
      if (s === "RUNNING") return "badge-info";
      return "badge-light";
    },
    formatDateTime(dateStr) {
      if (!dateStr) return "-";
      var d = new Date(dateStr);
      return d.toLocaleDateString("th-TH") + " " + d.toLocaleTimeString("th-TH");
    },
  },
};
</script>

<style scoped>
.label-required::after {
  content: " *";
  color: #f44;
}
</style>



// WEBPACK FOOTER //
// App/Components/Monitoring/MonitoringSendEmailReportAgent.vue